/**
 * TabInternational — the "International" tab body on UniversityDetail.
 *
 * Extracted from UniversityDetail.jsx as part of the P2 refactor. Renders
 * three panels:
 *   • International cohort + expat readiness (uni-level facts)
 *   • Where students come from (HESA sending-country breakdown)
 *   • UK Home Fee Status policy for UK-nationals living abroad
 *
 * Pure presentation — no local state, no fetches. Behaviour is identical
 * to the inline block it replaced.
 */
import { Card } from "@/components/ui/card";
import { Users, PlaneTakeoff } from "lucide-react";

function DataRow({ k, v }) {
  return (
    <div className="data-row">
      <div className="data-key">{k}</div>
      <div className="data-val">{v}</div>
    </div>
  );
}

export default function TabInternational({ uni }) {
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="p-6 border-stone-200">
          <div className="label-uppercase flex items-center gap-2">
            <Users className="h-3 w-3" /> International cohort
          </div>
          <div className="mt-4">
            <DataRow k="International %" v={`${(uni.international_pct * 100).toFixed(0)}%`} />
            <DataRow k="Main nationalities" v={(uni.main_nationalities || []).join(" · ")} />
            <DataRow k="Work during study" v={uni.work_rights_during} />
            <DataRow
              k="Post-study visa"
              v={`${uni.work_rights_after} (${uni.graduate_visa_years} yrs)`}
            />
          </div>
        </Card>
        <Card className="p-6 border-stone-200">
          <div className="label-uppercase flex items-center gap-2">
            <PlaneTakeoff className="h-3 w-3" /> UAE / expat readiness
          </div>
          <div className="mt-4">
            <DataRow k="Halal food" v={uni.halal_food ? "Available" : "Limited"} />
            <DataRow k="Prayer spaces" v={uni.prayer_spaces ? "On campus" : "Off campus only"} />
            <DataRow k="Airport arrival help" v={uni.arrival_support ? "Yes" : "No"} />
            <DataRow k="Holiday accommodation" v={uni.holiday_accommodation ? "Yes" : "No"} />
          </div>
        </Card>
      </div>

      <WhereStudentsComeFrom uni={uni} />
      <HomeFeePolicy uni={uni} />
    </>
  );
}

function WhereStudentsComeFrom({ uni }) {
  const rows = uni.international_composition?.top_sending_countries || [];
  if (rows.length === 0) return null;
  const comp = uni.international_composition;

  return (
    <Card className="mt-8 p-6 border-stone-200" data-testid="where-from-card">
      <div className="label-uppercase flex items-center gap-2">
        <Users className="h-3 w-3" /> Where students come from
        <span className="text-[10px] font-normal text-stone-500 normal-case">
          · HESA {comp.academic_year || "2023-24"}
        </span>
      </div>
      <div className="mt-4 text-sm text-stone-700 mb-3">
        <b>{comp.total_international?.toLocaleString()}</b> international
        students ·{" "}
        <b>{Math.round((comp.pct_international || 0) * 100)}%</b> of total intake
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-stone-500 border-b border-stone-200">
              <th className="py-2 pr-4 font-medium">Country</th>
              <th className="py-2 pr-4 font-medium text-right">Students</th>
              <th className="py-2 pr-4 font-medium text-right">% of intake</th>
              <th
                className="py-2 pr-4 font-medium text-right"
                title="Share of this country's UK-wide cohort at this uni"
              >
                % of country's UK cohort
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr
                key={c.country}
                data-testid={`where-from-row-${c.country.toLowerCase().replace(/\s+/g, "-")}`}
                className="border-b border-stone-100"
              >
                <td className="py-2 pr-4 font-medium text-stone-900">{c.country}</td>
                <td className="py-2 pr-4 text-right tabular-nums">
                  {c.students.toLocaleString()}
                </td>
                <td className="py-2 pr-4 text-right tabular-nums text-stone-600">
                  {(c.pct_of_intake * 100).toFixed(1)}%
                </td>
                <td className="py-2 pr-4 text-right tabular-nums text-stone-600">
                  {(c.pct_of_country_cohort * 100).toFixed(1)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function HomeFeePolicy({ uni }) {
  const p = uni.home_fee_policy;
  if (!p?.returning_uk_stance) return null;
  const badgeCls =
    p.flex_indicator === "flexible"
      ? "bg-emerald-100 text-emerald-800"
      : p.flex_indicator === "strict"
      ? "bg-red-100 text-red-800"
      : "bg-amber-100 text-amber-800";

  return (
    <Card className="mt-6 p-6 border-stone-200" data-testid="home-fee-policy-card">
      <div className="flex items-center justify-between">
        <div className="label-uppercase">Home fee status · UK-nationals living abroad</div>
        <span
          data-testid={`fee-flex-${p.flex_indicator}`}
          className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${badgeCls}`}
        >
          {p.flex_indicator}
        </span>
      </div>
      <p className="mt-3 text-sm text-stone-800 leading-relaxed">
        {p.returning_uk_stance}
      </p>
      {p.notes && (
        <p className="mt-2 text-xs text-stone-500 italic">{p.notes}</p>
      )}
      <a
        href={p.policy_url}
        target="_blank"
        rel="noreferrer"
        data-testid="home-fee-policy-link"
        className="mt-3 inline-block text-xs text-[#4C1D95] underline underline-offset-2 hover:text-[#3B0F7A]"
      >
        Official fee status policy →
      </a>
    </Card>
  );
}
